import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Bike, Gauge, Settings2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Rentals({ onBookNow }) {
  const { t } = useTranslation();
  const [motos, setMotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/motos`)
      .then((r) => setMotos(r.data.motos || []))
      .catch(() => setMotos([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section data-testid="rentals-page" className="bg-brand-black py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="mb-12 max-w-3xl">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand-orange">
            {t("nav.rentals")}
          </span>
          <h1 className="mt-2 font-display text-5xl uppercase leading-[0.95] sm:text-6xl lg:text-7xl">
            {t("rentals.title")}
          </h1>
          <p className="mt-4 text-base text-white/75 sm:text-lg">{t("rentals.subtitle")}</p>
        </div>

        {loading ? (
          <div className="text-center text-white/60">{t("rentals.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {motos.map((m, i) => (
              <motion.article
                key={m.id}
                data-testid={`rental-card-${m.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative flex flex-col border-2 border-brand-orange bg-brand-black transition hover:translate-x-[-4px] hover:translate-y-[-4px] hard-shadow"
              >
                <div className="relative aspect-square overflow-hidden bg-white">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                  />
                  <div className="absolute left-3 top-3 bg-brand-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                    {m.category}
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-display text-3xl uppercase leading-none">{m.name}</h3>
                  <ul className="mt-3 flex flex-wrap gap-3 text-xs font-bold uppercase tracking-widest text-white/70">
                    <li className="inline-flex items-center gap-1.5"><Bike className="h-3.5 w-3.5 text-brand-orange" />{m.engine}</li>
                    <li className="inline-flex items-center gap-1.5"><Settings2 className="h-3.5 w-3.5 text-brand-orange" />{m.transmission}</li>
                  </ul>
                  <p className="mt-3 text-sm text-white/80">
                    <strong className="font-bold uppercase tracking-wider text-white">
                      {t("rentals.bestFor")}:{" "}
                    </strong>
                    {m.best_for}
                  </p>

                  <div className="mt-auto pt-5">
                    <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-4">
                      <div>
                        <div className="font-display text-3xl text-brand-orange">${m.price_day_usd}</div>
                        <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
                          {t("rentals.perDay")}
                        </div>
                      </div>
                      <div>
                        <div className="font-display text-3xl text-white">${m.price_week_usd}</div>
                        <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
                          {t("rentals.perWeek")}
                        </div>
                      </div>
                      {m.price_month_usd && (
                        <div>
                          <div className="font-display text-3xl text-white">${m.price_month_usd}</div>
                          <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
                            {t("rentals.perMonth")}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button
                      data-testid={`rental-reserve-btn-${m.id}`}
                      onClick={() => onBookNow?.(m.name)}
                      className="mt-4 h-12 w-full rounded-none bg-brand-black font-display text-lg uppercase tracking-wider text-white hover:bg-brand-orange"
                    >
                      {t("rentals.reserve")} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
